/**
 * SN_SFA_ISSUE_REG_007 — AI 메뉴디자이너 (재설계, 1단계 현황). 2026-09-29.
 * 2026-10-01 미리보기 초기값 · 콤보 — 실제 메뉴와 같은 buildSearchFormData · getInitForm(디자인 폴백)의 js_combo 를 쓴다.
 *            전에는 빈 값 묶음을 넘겨 global(로그인 사업장) · 기간구분 같은 초기값이 안 실리고 콤보가 비었다.
 * 2026-10-02 미리보기 조회조건 끌어 옮기기 · X 삭제 · 크기 조정 → 초안(공통 SearchPanel designMode · onControlsChange). 우클릭 [설정] · [편집 완료] · [편집 취소]
 * 2026-10-02 화면 [저장] 버튼 삭제 — 툴바 저장과 같은 기능이고, 툴바는 메뉴권한(수정)을 따른다. [템플릿] → [템플릿 참조]
 * 2026-10-06 미리보기 초기값은 칸의 종류 · 파라미터 · 기본값이 바뀔 때만 새로 만든다 — 순서 · 크기만 바꿔도 칸이 전부 다시 그려졌다
 *
 * 들어오는 길 : 이슈등록(SN_SFA_ISSUE_REG_002)의 [DESIGN] 버튼 → nodeData { no_issue, cd_menu, cd_emp_admin }.
 * 이슈번호는 이슈등록이 채번한다. 여기서는 그 이슈에 화면 정의(초안)를 붙인다.
 *
 * 대화는 오른쪽 AI Bot 창에서 한다 — 이 화면에 채팅칸이 없다.
 *   화면이 이슈번호를 챗봇 화면정보(chatScreenSelections)에 올려 두면 AI Bot 이 /api/chat/v2 에 실어 보내고,
 *   제안이 오면(designProposal) BE 가 초안에 저장한 뒤 designApi 알림으로 이 화면이 초안을 다시 읽는다.
 *
 * 모양은 기존 등록 화면(SN_MAS_BAS_REG_001)과 같다 — SnTab + SnTabHeader 버튼 + SnGrid.
 *   미리보기(공용 페이지 LayoutRuntime 을 초안으로) · 조회조건 · 그리드 칼럼 · 쿼리 · 배치.
 *   조회조건 · 칼럼 편집 그리드의 칼럼 정의는 기존 정의 관리 화면(SN_SYS_BAS_REG_006 · 007)의 것을 그대로 쓴다.
 * 버튼   : 템플릿 · 초기화 · 저장 · 배포(점검 → 노츠메뉴 도움창으로 대상 고르기 → 본 테이블로 → 메뉴 열기). 탭용은 탭 줄(＋ 🗑 · 쿼리 실행).
 *
 * 기존 SN_SFA_ISSUE_REG_006(aiStudio) 은 건드리지 않는다. 이 화면이 새 버전이다.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { globalMenu } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnButton from "@shared/components/SnButton";
import { Button, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import { buildSearchFormData, getInitFormData } from "@shared/utils/CommonUtils";
import { getConvertedDate } from "@shared/utils/DateUtils";
import {
  chooseDesignTemplate,
  DefRow,
  deployDesignDraft,
  DESIGNER_MENU,
  DesignDraft,
  loadDesignDraft,
  MAIN_QUERY,
  previewDesignQuery,
  registerDesignFlush,
  resetDesignDraft,
  saveDesignDraft,
  subscribeDesignChanged,
} from "@shared/api/designApi";
import { LayoutRuntime, LayoutRuntimeRef } from "@menu/COM/SN_COM_LAYOUT/SN_COM_LAYOUT";
import TemplatePicker from "./TemplatePicker";
import DeployPopup from "./DeployPopup";
import { aiScreenOpen } from "@shared/libs/aiScreenOpen";

interface Props {
  Menuprops: menuInfo;
}

/** 조회조건 · 그리드 칼럼 편집 그리드의 칼럼 정의를 가져오는 기존 메뉴 */
const CTRL_DEF_MENU = "SN_SYS_BAS_REG_006";
const GRID_DEF_MENU = "SN_SYS_BAS_REG_007";

const SN_SFA_ISSUE_REG_007 = forwardRef(({ Menuprops }: Props, ref) => {
  const noIssue: string = Menuprops?.nodeData?.no_issue || "";
  const cdMenu: string = Menuprops?.cd_menu || DESIGNER_MENU;

  const [draft, setDraft] = useState<DesignDraft | null>(null);
  const [defColumns, setDefColumns] = useState<{ ctrl: any[]; grid: any[] }>({ ctrl: [], grid: [] });

  /* 서버가 보낸 이유("이미 배포된 초안은 …")를 그대로 보여 준다. apiFetch 는 오류창을 띄우지 않고 ApiError 로 던진다 —
     삼키면 "확인을 눌렀는데 아무 반응이 없다" 가 된다 (2026-09-30 초기화 시험) */
  const fail = async (e: any, fallback = "요청을 처리하지 못했습니다.") => {
    await showDialog("error", e?.message || fallback);
  };
  /** 조회 쿼리를 마지막으로 돌려 성공했는가 — 배포 점검 항목. AI 가 초안을 바꾸면 다시 돌려야 한다 */
  const [lastRunOk, setLastRunOk] = useState(false);

  const runtimeRef = useRef<LayoutRuntimeRef>(null);
  const ctrlGridRef = useRef<any>(null);
  const colGridRef = useRef<any>(null);
  const queryRefs = useRef<Record<string, string>>({});

  /* ---------------------------------------------------------------- 초기 · AI Bot 연결 */
  const reload = useCallback(async () => {
    if (!noIssue) return;
    try {
      setDraft(await loadDesignDraft(noIssue));
      /* AI 가 초안(쿼리)을 바꿨을 수 있다 — 배포 전에 다시 돌려 봐야 한다 */
      setLastRunOk(false);
    } catch (e: any) {
      await fail(e);
    }
  }, [noIssue]);

  useEffect(() => {
    (async () => {
      if (!noIssue) {
        await showDialog("alter", "이슈번호가 없습니다. 이슈등록에서 [DESIGN] 으로 들어와 주세요.");
        return;
      }
      try {
        const [d, ctrlDef, gridDef] = await Promise.all([
          loadDesignDraft(noIssue),
          getInitFormData({ cd_menu: CTRL_DEF_MENU, cd_module: "SYS" }),
          getInitFormData({ cd_menu: GRID_DEF_MENU, cd_module: "SYS" }),
        ]);
        setDraft(d);
        setDefColumns({
          ctrl: (ctrlDef?.grid?.grid1 || []).filter((c: any) => !["cd_menu", "cd_user", "cd_firm"].includes(c.cd_column)),
          grid: (gridDef?.grid?.grid1 || []).filter((c: any) => !["cd_menu", "cd_user", "cd_firm"].includes(c.cd_column)),
        });
      } catch (e: any) {
      await fail(e);
    }
    })();
  }, [noIssue]);

  /* AI Bot 이 이 이슈번호를 같이 보내게 올려 둔다. 화면을 닫으면 내린다 */
  useEffect(() => {
    if (!noIssue) return;
    globalMenu.getState().setChatScreenSelection(cdMenu, { no_issue: noIssue });
    return () => {
      globalMenu.getState().clearChatScreenSelection(cdMenu);
    };
  }, [cdMenu, noIssue]);

  /* AI Bot 이 제안을 받았다 — BE 가 초안에 저장했으니 다시 읽는다 */
  useEffect(() => subscribeDesignChanged((n) => {
    if (n === noIssue) void reload();
  }), [noIssue, reload]);

  /* AI Bot 이 말을 보내기 직전 — 안 저장된 편집(행 · 쿼리 · 칼럼 폭)을 조용히 저장해 AI 가 최신 초안을 보게 */
  const draftRef = useRef<DesignDraft | null>(null);
  draftRef.current = draft;
  useEffect(() => {
    if (!noIssue) return;
    return registerDesignFlush(noIssue, async () => {
      if (!draftRef.current?.stDesign) return;
      const d = collectDraft();
      if (d) setDraft(await saveDesignDraft(noIssue, d));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noIssue]);

  useEffect(() => {
    queryRefs.current = { ...(draft?.queries || {}) };
  }, [draft?.queries]);

  /* ---------------------------------------------------------------- 초안 → 미리보기 재료 */
  const grids = useMemo(() => {
    const out: Record<string, any[]> = {};
    (draft?.gridColumns || []).forEach((r) => {
      const id = String(r.cd_grid || "grid1");
      (out[id] = out[id] || []).push(r);
    });
    return out;
  }, [draft?.gridColumns]);

  const searchControls = useMemo(
    () => (draft?.controls || []).filter((r) => String(r.cd_form || "search") === "search"),
    [draft?.controls]
  );

  /* 콤보 목록 — 실제 메뉴는 getInitForm 이 칸마다 js_combo 를 붙여 준다. 초안 행에는 없어 콤보가 비어 보였다.
     getInitForm 은 이슈번호로 부르면 디자인 테이블(sys_design_*)을 같은 모양으로 읽는다(디자인 폴백).
     저장된 초안 기준이라 초안이 바뀔 때마다 다시 읽는다 (2026-10-01) */
  const [comboByParam, setComboByParam] = useState<Record<string, any>>({});
  useEffect(() => {
    if (!noIssue || !draft?.stDesign) return;
    let alive = true;
    getInitFormData({ cd_menu: noIssue })
      .then((rtn: any) => {
        if (!alive) return;
        const map: Record<string, any> = {};
        (rtn?.formControlData || []).forEach((c: any) => {
          if (c?.cd_param && c.js_combo != null) map[c.cd_param] = c.js_combo;
        });
        setComboByParam(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [noIssue, draft?.stDesign, draft?.controls]);

  const previewControls = useMemo(
    () => searchControls.map((c: any) => (c.js_combo == null && comboByParam[c.cd_param] != null
      ? { ...c, js_combo: comboByParam[c.cd_param] } : c)),
    [searchControls, comboByParam]
  );

  /* 초기값 — 실제 메뉴와 같은 공통 함수로 만든다. global = 로그인 사원 값(사업장 등), 기간 칸은 '구분,시작,끝' 을 나눠 넣는다.
     빈 칸도 "" 로 실려 쿼리 빈칸이 null 이 되지 않는다 (2026-10-01) */
  /* 초기값은 칸의 종류 · 파라미터 · 기본값으로만 정해진다 — 그것이 바뀔 때만 새로 만든다.
     배치 편집으로 순서 · 크기만 바꿔도 새로 만들면 미리보기가 칸을 전부 다시 그려 끌어 놓은 칸이 깜빡였다 (2026-10-06) */
  const formDataKey = useMemo(
    () => previewControls.map((c: any) => `${c.tp_ctrl}|${c.cd_param}|${c.cd_value ?? ""}`).sort().join("\n"),
    [previewControls]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const previewFormData = useMemo(() => buildSearchFormData(previewControls), [formDataKey]);

  const onPreviewSearch = useCallback(
    async (params: Record<string, any>) => {
      if (!draft?.queries?.[MAIN_QUERY]) {
        await showDialog("alter", "메인 조회 쿼리(main_01)가 없습니다.");
        return undefined;
      }
      try {
        const data = await previewDesignQuery(noIssue, MAIN_QUERY, params);
        setLastRunOk(true);
        return data;
      } catch (e: any) {
        await fail(e, "미리보기 조회에 실패했습니다. 쿼리를 확인해 주세요.");
        return undefined;
      }
    },
    [draft?.queries, noIssue]
  );

  /* ---------------------------------------------------------------- 편집 화면 → 초안 */
  /** 그리드의 지금 행 전부(고정 행 제외). 그리드가 아직 없으면 undefined */
  const rowsOf = (g: any): DefRow[] | undefined => {
    if (!g?.forEachNode) return undefined;
    g.stopEditing?.();
    const rows: DefRow[] = [];
    g.forEachNode((n: any) => {
      if (!n.rowPinned && n.data) rows.push(n.data);
    });
    return rows;
  };

  const collectDraft = (): DesignDraft | null => {
    if (!draft) return null;
    const controls = rowsOf(ctrlGridRef.current) ?? draft.controls;
    let gridColumns = rowsOf(colGridRef.current) ?? draft.gridColumns;
    /* 미리보기에서 끌어 놓은 칼럼 폭을 너비(qt_width)에 — 공용 그리드에 이벤트를 더하지 않고 저장 때 읽는다 */
    const widths = runtimeRef.current?.getColumnWidths?.() ?? {};
    gridColumns = gridColumns.map((r) => {
      const w = widths[String(r.cd_grid || "grid1")]?.[String(r.cd_column)];
      return w && w !== Number(r.qt_width) ? { ...r, qt_width: w } : r;
    });
    const queries: Record<string, string> = {};
    Object.entries(queryRefs.current).forEach(([k, v]) => {
      if (k && v && v.trim()) queries[k] = v;
    });
    return { ...draft, controls, gridColumns, queries };
  };

  /**
   * 미리보기 조회조건 편집 결과 → 초안 (2026-10-02).
   * 보이는 칸 = 그 순서대로 순서(no_seq) · 너비 · 높이, X 로 지운 칸 = 초안에서 그 줄 삭제(디자이너의 삭제는 진짜 삭제).
   * 조회조건 탭에 바로 보이고, DB 저장은 툴바 저장. 조회조건 탭에서 고치던 것도 함께 담으려고 collectDraft 를 바탕으로 한다.
   */
  const onSearchControlsEdited = (visible: any[], hidden: any[]) => {
    const d = collectDraft();
    if (!d) return;
    const isSearch = (r: any) => String(r.cd_form || "search") === "search";
    const order = new Map(visible.map((c, i) => [String(c.no_line), { seq: i + 1, colspan: c.qt_colspan, rowspan: c.qt_rowspan }]));
    const removed = new Set(hidden.map((c) => String(c.no_line)));
    const controls = d.controls
      .filter((r) => !(isSearch(r) && removed.has(String(r.no_line))))
      .map((r) => {
        if (!isSearch(r)) return r;
        const o = order.get(String(r.no_line));
        return o ? { ...r, no_seq: o.seq, qt_colspan: o.colspan, qt_rowspan: o.rowspan } : r;
      })
      .sort((a, b) => (isSearch(a) && isSearch(b) ? Number(a.no_seq) - Number(b.no_seq) : 0));
    setDraft({ ...d, controls });
  };

  /* ---------------------------------------------------------------- 버튼 */
  const onTemplate = async () => {
    const result: any = await showPopupModal({
      title: "화면 모양 고르기",
      width: "92%",
      height: "92%",
      children: <TemplatePicker />,
    });
    const picked = Array.isArray(result) ? result[0] : null;
    if (!picked?.cd_menu) return;
    try {
      setDraft(await chooseDesignTemplate(noIssue, picked.cd_menu, draft?.nmMenu || undefined));
    } catch (e: any) {
      await fail(e);
    }
  };

  const onSave = async () => {
    const d = collectDraft();
    if (!d) return false;
    if (!d.stDesign) {
      await showDialog("alter", "먼저 [템플릿 참조] 로 화면 모양을 고르거나 AI Bot 에 어떤 화면인지 말씀해 주세요.");
      return false;
    }
    try {
      setDraft(await saveDesignDraft(noIssue, d));
      await showDialog("success", "초안을 저장했습니다.");
      return true;
    } catch (e: any) {
      await fail(e, "초안을 저장하지 못했습니다.");
      return false;
    }
  };

  const onSearch = () => runtimeRef.current?.onSearch();

  /* ---------------------------------------------------------------- 쿼리 탭 — 파라미터 (DBeaver 처럼 채워서 실행) */
  /** 쿼리 본문이 바뀔 때마다 올린다 — :이름 목록을 다시 뽑는 신호 */
  const [queryVersion, setQueryVersion] = useState(0);
  /** :이름 → 값. 처음 값은 조회조건의 기본값(기간 0/0/F → 이달 1일 등)에서 */
  const [params, setParams] = useState<Record<string, string>>({});
  const [paramsOpen, setParamsOpen] = useState(true);

  /** 쿼리 전체에서 :이름 을 순서대로. :cd_firm 은 서버가 넣으니 뺀다. '::text' 같은 형변환은 이름이 아니다 */
  const paramNames = useMemo(() => {
    const names: string[] = [];
    const seen = new Set<string>();
    const all = Object.values({ ...(draft?.queries || {}), ...queryRefs.current }).join("\n");
    const re = /(^|[^:])(:)([a-zA-Z_][a-zA-Z0-9_]*)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(all)) !== null) {
      const n = m[3];
      if (n === "cd_firm" || seen.has(n)) continue;
      seen.add(n);
      names.push(n);
    }
    return names;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.queries, queryVersion]);

  /** 조회조건 정의에서 파라미터 기본값 — cd_param 'dt_from,dt_to' 와 cd_value '0/0/F,0/0/0' 를 짝지어 날짜로 */
  const defaultParams = useMemo(() => {
    const out: Record<string, string> = {};
    (draft?.controls || []).forEach((c) => {
      const keys = String(c.cd_param || "").split(",").map((s) => s.trim()).filter(Boolean);
      const vals = String(c.cd_value || "").split(",").map((s) => s.trim());
      keys.forEach((k, i) => {
        const v = vals[i] ?? "";
        out[k] = v.includes("/") ? String(getConvertedDate(v) ?? "") : v;
      });
    });
    return out;
  }, [draft?.controls]);

  useEffect(() => {
    setParams((prev) => {
      const next: Record<string, string> = {};
      paramNames.forEach((n) => {
        next[n] = prev[n] ?? defaultParams[n] ?? "";
      });
      return next;
    });
  }, [paramNames, defaultParams]);

  /* [실행] — 고친 쿼리를 저장한 뒤 파라미터 값으로 돌려 결과를 아래 그리드에 보인다.
     미리보기 조회와 같은 길(읽기 전용 · SELECT 만 · 200건 · 10초). 문장이 여럿이면 결과도 여럿 */
  const [queryResult, setQueryResult] = useState<{ columns: DefRow[]; rows: DefRow[] }[]>([]);
  const onRunQuery = async () => {
    const d = collectDraft();
    if (!d?.stDesign) return;
    if (!d.queries?.[MAIN_QUERY]) {
      await showDialog("alter", "메인 조회 쿼리(main_01)가 없습니다.");
      return;
    }
    try {
      setDraft(await saveDesignDraft(noIssue, d));
      const data = await previewDesignQuery(noIssue, MAIN_QUERY, params);
      setLastRunOk(true);
      setQueryResult(
        (data || []).map((rows) => {
          const keys = rows && rows.length > 0 ? Object.keys(rows[0]) : [];
          return {
            rows: rows || [],
            columns: keys.map((k, i) => ({
              cd_grid: "result", cd_column: k, nm_column: k, tp_column: "SnTextBox", fg_visible: "Y", no_seq: i, qt_width: 120,
              yn_allowsorting: "Y", yn_fixed: "N", qt_groupindex: -1, yn_sum: "N", yn_system: "N", yn_drilldown: "N", yn_required: "N",
            })),
          };
        })
      );
    } catch (e: any) {
      setQueryResult([]);
      await fail(e, "쿼리 실행에 실패했습니다. 문장을 확인해 주세요.");
    }
  };

  /* 초안 초기화 — 이 이슈의 초안을 전부 지우고 "시작 전" 으로. 반영된 이슈는 서버가 막는다. Bot 대화는 그대로 */
  const onReset = async () => {
    if (!draft?.stDesign) {
      await showDialog("alter", "지울 초안이 없습니다.");
      return;
    }
    const ok = await showDialog("confirm", "이 이슈의 초안을 모두 지웁니다. 진행하시겠습니까?");
    if (!ok) return;
    try {
      setDraft(await resetDesignDraft(noIssue));
      queryRefs.current = {};
      await showDialog("success", "초안을 지웠습니다. [템플릿 참조] 로 고르거나 AI Bot 에 다시 말씀해 주세요.");
    } catch (e: any) {
      await fail(e);
    }
  };

  /* 배포 — 안 저장된 편집을 먼저 저장 → 팝업(점검 → 대상 → 배포) → 서버 배포 → 메뉴 열기 (01_결정.md 20 · 21) */
  const onDeploy = async () => {
    if (!draft?.stDesign) {
      await showDialog("alter", "초안이 없습니다.");
      return;
    }
    const d = collectDraft();
    if (!d) return;
    let saved: DesignDraft;
    try {
      saved = await saveDesignDraft(noIssue, d);
      setDraft(saved);
    } catch (e: any) {
      await fail(e, "초안을 저장하지 못했습니다.");
      return;
    }
    const result: any = await showPopupModal({
      title: saved.stDesign === "DEPLOY" ? "재배포" : "배포",
      width: "56%",
      height: "72%",
      /* 팝업의 [확인] 이 배포 — 누르면 DeployPopup.checkValidity 가 점검 · 덮어쓰기 확인을 먼저 한다 */
      validation: true,
      children: <DeployPopup draft={saved} lastRunOk={lastRunOk} />,
    });
    const picked = Array.isArray(result) ? result[0] : null;
    if (!picked?.cd_menu) return;
    try {
      const after = await deployDesignDraft(noIssue, picked.cd_menu, picked.cd_query);
      setDraft(after);
      const open = await showDialog("confirm", `${picked.cd_menu} 메뉴에 배포했습니다. 지금 열어 볼까요?`);
      if (open) {
        const opened = await aiScreenOpen.run({ menuCode: picked.cd_menu });
        const note = aiScreenOpen.message(opened);
        if (note) await showDialog("alter", note);
      }
    } catch (e: any) {
      await fail(e);
    }
  };

  /* 조회조건 · 그리드 칼럼 행 추가 · 삭제 — 기존 정의 관리 화면과 같은 SnTabHeader 버튼 */
  const onCtrlBtnClick = (id: string) => {
    const g = ctrlGridRef.current;
    if (!g) return;
    if (id === "addRow") {
      const n = (g.getDisplayedRowCount?.() ?? 0) + 1;
      g.addRow({ cd_form: "search", no_line: n, no_seq: n, tp_ctrl: "SnTextBox", fg_visible: "Y", yn_required: "N" }, false, true);
    } else if (id === "deleteRow") {
      g.deleteSelectedRows?.();
    }
  };

  const onColBtnClick = (id: string) => {
    const g = colGridRef.current;
    if (!g) return;
    if (id === "addRow") {
      const n = (g.getDisplayedRowCount?.() ?? 0) + 1;
      g.addRow({ cd_grid: "grid1", no_seq: n, tp_column: "SnTextBox", fg_visible: "Y", qt_width: 120 }, false, true);
    } else if (id === "deleteRow") {
      g.deleteSelectedRows?.();
    }
  };

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));

  /* ---------------------------------------------------------------- 그리기 */
  const queryIds = Object.keys(draft?.queries || {});
  if (!queryIds.includes(MAIN_QUERY)) queryIds.unshift(MAIN_QUERY);

  /* 배포한 뒤 초기화하거나 다시 초안을 만들면 대상 메뉴는 남는다 — 그것도 보여 준다 */
  const status = draft?.stDesign === "DEPLOY"
    ? `배포됨 · ${draft?.cdMenu}`
    : `${draft?.stDesign ? "초안" : "시작 전"}${draft?.cdMenu ? ` · 배포됨 ${draft.cdMenu}` : ""}`;
  const fg = draft?.fgMenu === "REP" ? "현황" : draft?.fgMenu === "REG" ? "등록" : "-";

  /* 화면 전체 버튼은 이슈 정보 줄에, 탭마다 다른 버튼은 그 탭의 제목 줄(SnTabHeader)에 — 기존 등록 화면과 같은 자리 */
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1 px-1 text-sm flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="font-semibold">이슈 {noIssue}</span>
          {draft?.nmMenu && <span>{draft.nmMenu}</span>}
          <span className="text-gray-500 font-mono text-xs">
            {draft?.cdTemplate || "템플릿 없음"} · {fg} · {status}
          </span>
        </div>
        <div className="relative flex space-x-2">
          <SnButton text="템플릿 참조" onClick={onTemplate} />
          <SnButton text="초기화" onClick={onReset} />
          <SnButton text={draft?.stDesign === "DEPLOY" ? "재배포" : "배포"} onClick={onDeploy} />
        </div>
      </div>

      <div className="w-full flex h-full min-h-0">
        <SnTab
          titles={["미리보기", "조회조건", "그리드 칼럼", "쿼리"]}
          width={"100%"}
          header={[
            /* 미리보기 조회는 헤더 돋보기(onSearch)로 — 기존 화면과 같다. 탭용 버튼은 전부 아이콘(＋ 🗑 ▶) */
            <SnTabHeader key="h-preview" />,
            <SnTabHeader key="h-ctrl" onBtnAddClick={onCtrlBtnClick} onBtnDeleteClick={onCtrlBtnClick} />,
            <SnTabHeader key="h-cols" onBtnAddClick={onColBtnClick} onBtnDeleteClick={onColBtnClick} />,
            <SnTabHeader
              key="h-query"
              body={
                /* 편집기 옆에 있던 버튼 그대로, 자리만 ＋ 🗑 위치로 */
                <Tooltip title="Ctrl+Enter">
                  <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} onClick={onRunQuery}
                          sx={{ py: 0, height: 30, minHeight: 30, fontSize: 13, textTransform: "none", mr: 2, "& .MuiSvgIcon-root": { fontSize: 22 } }}>
                    실행
                  </Button>
                </Tooltip>
              }
            />,
          ]}
          body={[
            <div key="preview" className="h-full min-h-0">
              {draft?.stDesign ? (
                <LayoutRuntime
                  ref={runtimeRef}
                  cdMenu={`DRAFT_${noIssue}`}
                  layout={draft.layout || []}
                  formData={previewFormData}
                  formControlData={previewControls}
                  grids={grids}
                  onSearch={onPreviewSearch}
                  onSearchControlsChange={onSearchControlsEdited}
                />
              ) : (
                <div className="p-4 text-gray-500">
                  [템플릿 참조] 로 화면 모양을 고르거나, 오른쪽 AI Bot 에 어떤 화면을 만들지 말씀해 주세요.
                </div>
              )}
            </div>,
            <SnGrid
              key="ctrl"
              ref={ctrlGridRef}
              columnConfig={defColumns.ctrl}
              rowData={draft?.controls || []}
              etcProps={{ rowSelection: "single", isEditable: true }}
              events={{}}
            />,
            <SnGrid
              key="cols"
              ref={colGridRef}
              columnConfig={defColumns.grid}
              rowData={draft?.gridColumns || []}
              etcProps={{ rowSelection: "single", isEditable: true }}
              events={{}}
            />,
            <div key="query" className="h-full min-h-0 flex flex-col space-y-1.5 p-1">
              <SnResizeBar direction={"height"} id="designQueryEdit" orgSize="50%">
                <div className="h-full min-h-0 flex flex-col gap-1">
                  {queryIds.map((id) => (
                    <div key={id} className={`flex flex-col min-h-0 ${id === MAIN_QUERY ? "flex-[3]" : "flex-1"}`}>
                      <div className="text-xs text-gray-600 mb-0.5">
                        <span className="font-semibold">{id === MAIN_QUERY ? "조회 쿼리" : `쿼리 ${id}`}</span>
                        {id === MAIN_QUERY && <span className="ml-2 text-gray-400">▶ 또는 Ctrl+Enter 로 실행</span>}
                      </div>
                      <textarea
                        className="flex-1 min-h-[80px] border rounded p-1 font-mono text-xs resize-none"
                        defaultValue={draft?.queries?.[id] || ""}
                        onChange={(e) => {
                          queryRefs.current[id] = e.target.value;
                          setQueryVersion((v) => v + 1);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            e.preventDefault();
                            onRunQuery();
                          }
                        }}
                      />
                    </div>
                  ))}
                  {/* 파라미터 — 쿼리의 :이름 을 한 줄에. 값을 고치고 [실행]. 접을 수 있다 */}
                  <div className="border rounded px-2 py-1 bg-gray-50">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <button type="button" className="font-semibold" onClick={() => setParamsOpen((o) => !o)}>
                        {paramsOpen ? "▾" : "▸"} 파라미터 {paramNames.length}개
                      </button>
                      <span>SELECT 만 · :cd_firm 은 서버가 넣음 · 문장을 ; 로 나누면 그리드 순서대로</span>
                    </div>
                    {paramsOpen && paramNames.length > 0 && (
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                        {paramNames.map((n) => (
                          <label key={n} className="flex items-center gap-1 text-xs">
                            <span className="font-mono text-gray-700">:{n}</span>
                            <input
                              className="border rounded px-1 h-6 w-28 text-xs font-mono"
                              value={params[n] ?? ""}
                              onChange={(e) => setParams((p) => ({ ...p, [n]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") onRunQuery();
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="designQueryResult" orgSize="50%">
                <div className="h-full min-h-0 flex flex-col gap-1">
                  {queryResult.length === 0 ? (
                    <div className="p-2 text-gray-500 text-sm">[실행] 을 누르면 결과가 여기에 보입니다.</div>
                  ) : (
                    queryResult.map((r, i) => (
                      <div key={i} className="flex-1 min-h-0 flex flex-col">
                        <div className="text-xs text-gray-600">{i + 1}번째 문장 — {r.rows.length}건{r.rows.length >= 200 ? " (200건까지만)" : ""}</div>
                        <div className="flex-1 min-h-0">
                          <SnGrid columnConfig={r.columns as any} rowData={r.rows} etcProps={{ rowSelection: "single" }} events={{}} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </SnResizeBar>
            </div>,
            /* 배치 탭은 뺐다 — 보기만 되고 할 수 있는 게 없었다(2026-09-30). 모양은 [템플릿 참조] 이나 AI Bot 으로 바꾼다.
               2단계(등록)에서 저장 설정(nm_table · 연결 칼럼 · 채번)을 넣을 때 "영역 · 저장 설정" 탭으로 되살린다 */
          ]}
        />
      </div>
    </div>
  );
});

export default SN_SFA_ISSUE_REG_007;
